import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Checkbox,
    FormControlLabel,
    Link,
    Alert,
} from '@mui/material';

interface RegisterData {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    confirmPassword: string;
}

interface RegisterFormProps {
    open: boolean;
    loading: boolean;
    registerData: RegisterData;
    setRegisterData: React.Dispatch<React.SetStateAction<RegisterData>>;
    onClose: () => void;
    onSubmit: () => void | Promise<any>;
    error?: string | null;
}

const RegisterForm: React.FC<RegisterFormProps> = ({
    open,
    loading,
    registerData,
    setRegisterData,
    onClose,
    onSubmit,
    error,
}) => {
    const [ndaChecked, setNdaChecked] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    return (
        <Dialog open={open} onClose={() => !loading && onClose()} maxWidth='sm' fullWidth>
            <DialogTitle>Register New User</DialogTitle>
            <Box
                component='form'
                onSubmit={async (e: React.FormEvent<HTMLFormElement>) => {
                    e.preventDefault();
                    console.log('[RegisterForm] Submit triggered');
                    try {
                        const result = onSubmit();
                        if (result && typeof (result as any).then === 'function') {
                            await result;
                        }
                        setSubmitted(true);
                    } catch (err) {
                        console.error('[RegisterForm] submit error', err);
                    }
                }}
            >
                <DialogContent sx={{ paddingTop: 2 }}>
                    <TextField
                        fullWidth
                        label='First Name'
                        value={registerData.firstName}
                        onChange={(e) => setRegisterData({ ...registerData, firstName: e.target.value })}
                        margin='normal'
                        autoComplete='given-name'
                        disabled={loading}
                    />
                    <TextField
                        fullWidth
                        label='Last Name'
                        value={registerData.lastName}
                        onChange={(e) => setRegisterData({ ...registerData, lastName: e.target.value })}
                        margin='normal'
                        autoComplete='family-name'
                        disabled={loading}
                    />
                    <TextField
                        fullWidth
                        label='Email'
                        type='email'
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        margin='normal'
                        autoComplete='email'
                        disabled={loading}
                    />
                    <TextField
                        fullWidth
                        label='Password'
                        type='password'
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        margin='normal'
                        autoComplete='new-password'
                        disabled={loading}
                    />
                    <TextField
                        fullWidth
                        label='Confirm Password'
                        type='password'
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                        margin='normal'
                        autoComplete='new-password'
                        disabled={loading}
                    />
                </DialogContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', padding: '8px 16px' }}>
                        <FormControlLabel
                            control={<Checkbox checked={ndaChecked} onChange={(e) => setNdaChecked(e.target.checked)} />}
                            label={<span>Check to accept the <Link href="/assets/nda.html" target="_blank" rel="noopener">NDA</Link></span>}
                        />
                    </Box>
                    {error && (
                        <Box sx={{ px: 2, pb: 1 }}>
                            <Alert severity='error'>{error}</Alert>
                        </Box>
                    )}

                    {submitted && (
                        <Box sx={{ px: 2, pb: 1 }}>
                            <Alert severity='info'>
                                Xealed platform is invitation-only. Your request will be considered for approval. In the meantime, you have access to limited information.
                            </Alert>
                        </Box>
                    )}
                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        type='submit'
                        variant='contained'
                        color='primary'
                        disabled={loading || !ndaChecked || submitted}
                        onClick={() => console.log('[RegisterForm] Register button clicked')}
                    >
                        {loading ? 'Registering...' : 'Register'}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export default RegisterForm;
